import React, { useState, useEffect, useMemo } from 'react';
import {
    View,
    ActivityIndicator,
    StyleSheet,
    Dimensions,
    Text,
    Alert,
} from 'react-native';
import Pdf from 'react-native-pdf';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Loading } from "@shared/components/loading";


interface PdfViewerProps {
    pdfUrl: string;
    onLoadStart?: () => void;
    onLoadComplete?: (pages: number) => void;
    onError?: (error: any) => void;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
    pdfUrl,
    onLoadStart,
    onLoadComplete,
    onError,
}) => {
    const navigation = useNavigation();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(!!pdfUrl);
    const [hasStartedLoading, setHasStartedLoading] = useState(false);

    useEffect(() => {
        if (pdfUrl) {
            setIsLoading(true);
            setHasStartedLoading(false);
        } else {
            setIsLoading(false);
        }
    }, [pdfUrl]);

    useEffect(() => {
        if (isLoading && hasStartedLoading) {
            const timeout = setTimeout(() => {
                setIsLoading(false);
            }, 10000);

            return () => clearTimeout(timeout);
        }
    }, [isLoading, hasStartedLoading]);

    const source = useMemo(() => ({
        uri: pdfUrl,
        cache: false,
    }), [pdfUrl]);

    const handleLoadComplete = (pages: number) => {
        setIsLoading(false);
        onLoadComplete?.(pages);
    };

    const handleError = (error: any) => {
        setIsLoading(false);
        onError?.(error);

        Alert.alert(
            t('commons.errors.generic'),
            t('commons.loading.loadError'),
            [{ text: 'OK', onPress: () => navigation.goBack() }]
        );
    };

    const handleLoadProgress = (percent: number) => {
        if (!hasStartedLoading) {
            setHasStartedLoading(true);
            onLoadStart?.();
        }
        if (percent > 0) {
            setIsLoading(false);
        }
    };

    return (
        <View className="flex-1 relative">
            {pdfUrl && (
                <Pdf
                    trustAllCerts={false}
                    source={source}
                    onLoadProgress={handleLoadProgress}
                    onLoadComplete={handleLoadComplete}
                    onError={handleError}
                    style={styles.pdf}
                />
            )}

            {isLoading && (
                <View style={styles.loadingOverlay}>
                    <Loading />
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    pdf: {
        flex: 1,
        width: Dimensions.get('window').width - 20,
        backgroundColor: '#FFFFFF',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: Dimensions.get('window').width,
        height: Dimensions.get('window').height,
        backgroundColor: '#FFFFFF',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
});
